import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { evaluateStrikeCountCounts } from './StrikeCountCounts';
import { evaluateStrikeRangeShift } from './StrikeRangeShift';

/**
 * CombinedCountAndShift — Combined Strike Count + Strike Range Shift Confluence
 *
 * This strategy evaluates both the Strike Count Counts strategy and the Strike Range Shift strategy.
 * It only returns a signal when BOTH strategies return a signal for the SAME trade direction (BULL/BEAR).
 */
export const evaluateCombinedCountAndShift: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    // Evaluate the underlying strategies (extract first leg if array)
    const rawCountSignal = evaluateStrikeCountCounts(data, currentUnixTime, config, context);
    const rawShiftSignal = evaluateStrikeRangeShift(data, currentUnixTime, config, context);
    
    const countSignal = rawCountSignal ? (Array.isArray(rawCountSignal) ? rawCountSignal[0] : rawCountSignal) : null;
    const shiftSignal = rawShiftSignal ? (Array.isArray(rawShiftSignal) ? rawShiftSignal[0] : rawShiftSignal) : null;

    // If NEITHER produces a signal, do nothing
    if (!countSignal && !shiftSignal) {
        return null;
    }

    // ── THE SAFETY VETO ──
    // If BOTH triggered but they are fighting (one says BULL, one says BEAR), stay out of the market.
    if (countSignal && shiftSignal && countSignal.trade_type !== shiftSignal.trade_type) {
        return null;
    }

    // At this point, either one strategy fired, or both fired in the exact same direction.
    // Grab the signal from whoever fired (if both fired, they agree, so either is fine).
    const activeSignal = countSignal || shiftSignal;

    // Build the remarks based on who triggered the trade
    let combinedRemarks = '';
    if (countSignal && shiftSignal) {
        combinedRemarks = `CONFLUENCE (${activeSignal!.trade_type}): Count [${countSignal.remarks}] & Shift [${shiftSignal.remarks}]`;
    } else if (countSignal) {
        combinedRemarks = `COUNT TRIGGER (${countSignal.trade_type}): ${countSignal.remarks}`;
    } else if (shiftSignal) {
        combinedRemarks = `SHIFT TRIGGER (${shiftSignal.trade_type}): ${shiftSignal.remarks}`;
    }

    return {
        trade_type: activeSignal!.trade_type,
        strike: activeSignal!.strike,
        option_type: activeSignal!.option_type,
        entry_price: activeSignal!.entry_price,
        remarks: combinedRemarks
    };
};
